/**
 * Encapsulation Logic - Creates definition from selected nodes
 *
 * Takes a set of nodes from a graph and creates a new definition,
 * replacing the nodes with an instance of that definition.
 */

import { type GraphDefinition, type ExposedEndpoint, type NodeConfig, getNodeType } from '@/graph/GraphDefinition';
import type { Connection } from '@/graph/Connection';

export interface EncapsulateParams {
  nodeIds: string[];         // Nodes to encapsulate
  newDefinitionId: string;      // Name for the new definition
  newInstanceId?: string;    // Name for the replacement instance
  preserveContainerApi?: boolean; // Keep exposed endpoints on container (default true)
}

export interface EncapsulateResult {
  definition: GraphDefinition;
  externalConnections: {
    incoming: Array<{ externalPath: string; exposedName: string }>;
    outgoing: Array<{ exposedName: string; externalPath: string }>;
  };
  // Exposed endpoints from container that need path updates
  affectedExposedEndpoints: Array<{
    containerExposedName: string;  // Name on container's API
    newSubgraphExposedName: string; // Name on new subgraph's API
  }>;
}

/**
 * Analyze selected nodes and build a definition structure
 */
export function analyzeForEncapsulation(
  containerDefinition: GraphDefinition,
  nodeIds: string[]
): EncapsulateResult {
  const selectedSet = new Set(nodeIds);

  // Build nodes map for the new definition
  const nodes: Record<string, NodeConfig | string> = {};
  for (const nodeId of nodeIds) {
    const nodeEntry = containerDefinition.nodes[nodeId];
    if (!nodeEntry) {
      throw new Error(`Node '${nodeId}' not found in source definition`);
    }
    nodes[nodeId] = nodeEntry;
  }

  // Categorize connections
  const internalConnections: Connection[] = [];
  const incomingExternal: Array<{ externalPath: string; internalPath: string }> = [];
  const outgoingExternal: Array<{ internalPath: string; externalPath: string }> = [];

  for (const conn of containerDefinition.connections) {
    const [sourceNode] = conn.source.split('.');
    const [targetNode] = conn.target.split('.');

    const sourceInside = selectedSet.has(sourceNode);
    const targetInside = selectedSet.has(targetNode);

    if (sourceInside && targetInside) {
      // Internal connection
      internalConnections.push(conn);
    } else if (!sourceInside && targetInside) {
      // Incoming external connection
      incomingExternal.push({
        externalPath: conn.source,
        internalPath: conn.target
      });
    } else if (sourceInside && !targetInside) {
      // Outgoing external connection
      outgoingExternal.push({
        internalPath: conn.source,
        externalPath: conn.target
      });
    }
    // If both outside, irrelevant to this encapsulation
  }

  // Build API from external connections
  // Use endpoint name as exposed name (e.g., 'clock.tick' -> 'tick')
  const api: Record<string, ExposedEndpoint> = {};
  const usedNames = new Set<string>();

  function getUniqueName(path: string): string {
    const endpoint = path.split('.').pop()!;
    let name = endpoint;
    let counter = 1;
    while (usedNames.has(name)) {
      name = `${endpoint}${counter++}`;
    }
    usedNames.add(name);
    return name;
  }

  const incomingApi: Array<{ externalPath: string; exposedName: string }> = [];
  const outgoingApi: Array<{ exposedName: string; externalPath: string }> = [];

  // Expose incoming endpoints (inputs to the definition)
  for (const { externalPath, internalPath } of incomingExternal) {
    const exposedName = getUniqueName(internalPath);
    api[exposedName] = { path: internalPath, isInput: true, isOutput: false };
    incomingApi.push({ externalPath, exposedName });
  }

  // Expose outgoing endpoints (outputs from the definition)
  for (const { internalPath, externalPath } of outgoingExternal) {
    const exposedName = getUniqueName(internalPath);
    api[exposedName] = { path: internalPath, isInput: false, isOutput: true };
    outgoingApi.push({ exposedName, externalPath });
  }

  // Handle exposed endpoints from container that point to encapsulated nodes
  // These need to be re-exposed on the new subgraph and the container's paths updated
  const affectedExposedEndpoints: Array<{
    containerExposedName: string;
    newSubgraphExposedName: string;
  }> = [];

  for (const [exposedName, exposed] of Object.entries(containerDefinition.api)) {
    const [pathNodeId] = exposed.path.split('.');
    if (selectedSet.has(pathNodeId)) {
      // This exposed endpoint points to a node being encapsulated
      // Re-expose it on the new subgraph using the endpointId
      const subgraphExposedName = getUniqueName(exposed.path);

      // Copy the exposed endpoint config to the new subgraph's API
      api[subgraphExposedName] = { ...exposed };

      affectedExposedEndpoints.push({
        containerExposedName: exposedName,
        newSubgraphExposedName: subgraphExposedName
      });
    }
  }

  // Build config from source config
  const config: Record<string, Record<string, any>> = {};
  for (const nodeId of nodeIds) {
    const nodeConfig = containerDefinition.config[nodeId];
    if (nodeConfig) {
      config[nodeId] = { ...nodeConfig };
    }
  }

  return {
    definition: {
      id: '', // Will be set by caller
      nodes,
      connections: internalConnections,
      api,
      config
    },
    externalConnections: {
      incoming: incomingApi,
      outgoing: outgoingApi
    },
    affectedExposedEndpoints
  };
}

/**
 * Apply encapsulation to a definition
 * Returns the modified source definition and the new definition
 */
export function encapsulate(
  containerDefinition: GraphDefinition,
  params: EncapsulateParams
): { newDefinition: GraphDefinition; modifiedContainerDefinition: GraphDefinition } {
  const { nodeIds, newDefinitionId, newInstanceId, preserveContainerApi = true } = params;
  // Node ID in the parent is the instance ID (unique), not the definition ID (type name)
  const nodeIdInParent = newInstanceId ?? newDefinitionId;

  const analysis = analyzeForEncapsulation(containerDefinition, nodeIds);
  const newDefinition = { ...analysis.definition, id: newDefinitionId };

  // Build modified source definition
  const modifiedNodes: Record<string, NodeConfig | string> = {};

  // Copy nodes that aren't being encapsulated
  for (const [nodeId, nodeEntry] of Object.entries(containerDefinition.nodes)) {
    if (!nodeIds.includes(nodeId)) {
      modifiedNodes[nodeId] = nodeEntry;
    }
  }

  // Add the new instance (just the type string for newly created instance)
  modifiedNodes[nodeIdInParent] = { type: newDefinitionId };

  // Build modified connections
  const modifiedConnections: Connection[] = [];

  // Keep connections between non-selected nodes
  for (const conn of containerDefinition.connections) {
    const [sourceNode] = conn.source.split('.');
    const [targetNode] = conn.target.split('.');

    if (!nodeIds.includes(sourceNode) && !nodeIds.includes(targetNode)) {
      modifiedConnections.push(conn);
    }
  }

  // Rewire incoming external connections to the new instance
  for (const { externalPath, exposedName } of analysis.externalConnections.incoming) {
    modifiedConnections.push({
      source: externalPath,
      target: `${nodeIdInParent}.${exposedName}`
    });
  }

  // Rewire outgoing external connections from the new instance
  for (const { exposedName, externalPath } of analysis.externalConnections.outgoing) {
    modifiedConnections.push({
      source: `${nodeIdInParent}.${exposedName}`,
      target: externalPath
    });
  }

  // Build modified config (remove encapsulated nodes' config)
  const modifiedConfig: Record<string, Record<string, any>> = {};
  for (const [nodeId, nodeConfig] of Object.entries(containerDefinition.config)) {
    if (!nodeIds.includes(nodeId)) {
      modifiedConfig[nodeId] = { ...nodeConfig };
    }
  }

  // Build modified API - update or remove paths for affected exposed endpoints
  const modifiedApi: Record<string, ExposedEndpoint> = {};
  for (const [exposedName, exposed] of Object.entries(containerDefinition.api)) {
    const affected = analysis.affectedExposedEndpoints.find(
      a => a.containerExposedName === exposedName
    );

    if (affected) {
      if (preserveContainerApi) {
        // Update path to point to the new subgraph's exposed endpoint
        modifiedApi[exposedName] = {
          ...exposed,
          path: `${nodeIdInParent}.${affected.newSubgraphExposedName}`
        };
      }
      // If not preserving, simply don't add it to modifiedApi (removes it)
    } else {
      modifiedApi[exposedName] = { ...exposed };
    }
  }

  const modifiedContainerDefinition: GraphDefinition = {
    ...containerDefinition,
    nodes: modifiedNodes,
    connections: modifiedConnections,
    config: modifiedConfig,
    api: modifiedApi
  };

  return { newDefinition, modifiedContainerDefinition };
}
